import { useQuery, useMutation } from '@tanstack/react-query';

import { getLoyalties } from '@hooks/cache';
import {
  LoyaltyType,
  ApiError,
  GetLoyaltyModel,
  BasketViewModel,
} from '@api/v3';
import { callService } from '@api/remote-call';

import {
  HopiModalFormData,
  HopiModalProps,
  SelectablePromotion,
} from './types';
import { promotionReducer } from '../helpers';

export const useHopiPromotions = ({
  itemsIds,
  basketInfo,
  shouldFetch,
  type,
}: {
  basketInfo?: BasketViewModel;
  itemsIds: string[];
  shouldFetch: boolean;
  type: 'Hotel' | 'Tour';
}) => {
  const activatedLoyalties = basketInfo?.loyaltyInfo?.activatedLoyalties ?? [];
  const basketId = basketInfo?.id;
  const itemId = itemsIds[0];
  const showPromotions = activatedLoyalties.includes(LoyaltyType.HOPI_KAZANIM);
  const { data = [] } = useQuery(
    ['hopiLoyalties', basketId, itemId, type],
    async () => {
      const response = await callService(
        {
          service: 'LoyaltiesService',
          method: 'getHopiVariations',
          version: 'v3',
        },
        {
          basketId,
          itemId,
          type,
        }
      );
      return response;
    },
    { enabled: shouldFetch, cacheTime: 5 * 1000 * 60 }
  );
  const entries = data.reduce((acc, variation) => {
    const selectHopiPromotions = promotionReducer<SelectablePromotion>(
      LoyaltyType.HOPI_KAZANIM,
      variation.selected,
      (p) => ({ ...p, variation, entityId: itemId, type })
    );
    return [
      ...acc,
      ...variation.promotions.reduce(
        selectHopiPromotions,
        [] as SelectablePromotion[]
      ),
    ];
  }, [] as SelectablePromotion[]);
  return { data: entries, showPromotions };
};

export const useHopiCodeValidation = ({
  onValidate,
  onValidateError,
  closeModal,
  basketId,
  refetchBasket,
}: Pick<
  HopiModalProps,
  'onValidate' | 'onValidateError' | 'closeModal' | 'basketId'
> & { refetchBasket: () => void }) => {
  const params = basketId ? { basketId } : {};
  const { mutate: handleHopiValidate, isLoading: isValidating } = useMutation<
    GetLoyaltyModel,
    ApiError,
    HopiModalFormData
  >(
    ['validateHopi'],
    async (form: HopiModalFormData) => {
      if (!form.hopiCode) {
        throw new Error('missing hopi code');
      }
      return callService(
        { service: 'LoyaltiesService', method: 'validateHopi', version: 'v3' },
        {
          item: form.hopiCode,
          type: 'TOKEN',
          ...params,
        }
      );
    },
    {
      onSuccess: ({ basketId: id }) => {
        if (id) {
          onValidate?.(id);
        }

        refetchBasket();
      },
      onError: (error) => {
        if (error.message === 'missing hopi code') {
          return onValidateError?.();
        }
        closeModal();
      },
    }
  );
  return {
    handleHopiValidate,
    isValidating,
    isValidated: !!getLoyalties()[LoyaltyType.HOPI_KAZANIM],
  };
};
